// The compact team table: select · team (logo, name, tag) · roster avatars · avg FACEIT ELO ·
// coach · owner (super admins) · action icons (roster, edit, archive/restore, delete). Clicking a row
// opens the roster panel.
import React from 'react';
import { useTranslation } from 'react-i18next';
import TeamLogo from './TeamLogo';
import CreatedBy from '../CreatedBy';

function PlayerDots({ players }) {
  const { t } = useTranslation();
  const main = players.filter(p => p.role !== 'sub');
  const subs = players.filter(p => p.role === 'sub');
  const shown = main.slice(0, 5);
  const extra = players.length - shown.length;
  if (!players.length) return <span className="tm-muted">{t('teams.noPlayers', 'No players')}</span>;
  return (
    <span className="tm-avatars" title={players.map(p => `${p.name || p.steam_id}${p.role === 'sub' ? ` (${t('teams.sub', 'Sub')})` : ''}`).join('\n')}>
      <span className="tm-faces">
        {shown.map(p => (
          <span key={p.id} className="tm-av">
            {p.avatar_v
              ? <img src={`/api/avatars/${p.steam_id}?v=${p.avatar_v}`} alt="" loading="lazy" />
              : String(p.name || '?').trim().charAt(0).toUpperCase()}
          </span>
        ))}
        {extra > 0 && <span className="tm-av more">+{extra}</span>}
      </span>
      <span className="tm-muted">
        {subs.length
          ? t('teams.mainSubCount', '{{main}} + {{subs}} sub', { main: main.length, subs: subs.length })
          : t('teams.playerCount', '{{count}} players', { count: players.length })}
      </span>
    </span>
  );
}

const TeamRow = React.memo(function TeamRow({ team, selected, active, showOwner, view, onToggle, onOpen, onAction }) {
  const { t } = useTranslation();
  const players = team.players || [];
  const elo = team.avg_faceit_elo;
  return (
    <div className={`tm-row tm-grid${showOwner ? '' : ' no-owner'}${active ? ' active' : ''}${selected ? ' selected' : ''}`}
      onClick={() => onOpen(team)} role="button" tabIndex={0} aria-label={t('teams.openRosterOf', 'Open roster of {{name}}', { name: team.name })}
      onKeyDown={e => { if (e.key === 'Enter' && e.target === e.currentTarget) onOpen(team); }}>
      <span className="c-chk" onClick={e => e.stopPropagation()}>
        <input type="checkbox" className="tm-check" checked={selected} onChange={() => onToggle(team.id)} aria-label={t('teams.selectTeam', 'Select team')} />
      </span>
      <span className="c-team tm-team">
        <TeamLogo team={team} size={34} />
        <span className="tm-team-name">{team.name}</span>
        {team.tag && <span className="tm-tag">{team.tag}</span>}
      </span>
      <span className="c-roster"><PlayerDots players={players} /></span>
      <span className="c-elo">{elo ? <span className="tm-elo" title={t('teams.avgEloTitle', 'Average FACEIT ELO of {{count}} linked players', { count: team.faceit_elo_count })}>{elo}</span> : <span className="tm-muted">—</span>}</span>
      <span className="c-coach tm-muted" title={team.coach_name || ''}>{team.coach_name || '—'}</span>
      {showOwner && <span className="c-owner tm-muted" onClick={e => e.stopPropagation()}><CreatedBy steamId={team.created_by_steam} name={team.created_by_name} /></span>}
      <span className="c-meta tm-muted">
        {elo ? <span className="tm-elo">{t('teams.eloShort', 'ELO {{elo}}', { elo })}</span> : null}
        {team.coach_name ? <span>{t('teams.coachLabel', 'Coach: {{name}}', { name: team.coach_name })}</span> : null}
      </span>
      <span className="c-menu tm-actions" onClick={e => e.stopPropagation()}>
        {/* every row action as a direct icon (no ⋯ menu); delete still asks first (Teams.js#onAction) */}
        <button type="button" className="tm-icon-btn tm-act" title={t('teams.roster', 'Roster')} aria-label={t('teams.roster', 'Roster')} onClick={() => onAction('roster', team)}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.5" /><path d="M2 20a7 7 0 0 1 14 0" /><path d="M16 4a3.5 3.5 0 0 1 0 7" /><path d="M18 14a7 7 0 0 1 4 6" /></svg>
        </button>
        <button type="button" className="tm-icon-btn tm-act" title={t('common.edit', 'Edit')} aria-label={t('common.edit', 'Edit')} onClick={() => onAction('edit', team)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
        </button>
        {view === 'archived' ? (
          <button type="button" className="tm-icon-btn tm-act" title={t('teams.restore', 'Restore')} aria-label={t('teams.restore', 'Restore')} onClick={() => onAction('archive', team)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></svg>
          </button>
        ) : (
          <button type="button" className="tm-icon-btn tm-act" title={t('teams.archive', 'Archive')} aria-label={t('teams.archive', 'Archive')} onClick={() => onAction('archive', team)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="5" rx="1" /><path d="M5 9v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9" /><path d="M10 13h4" /></svg>
          </button>
        )}
        <button type="button" className="tm-icon-btn tm-act danger" title={t('common.delete', 'Delete')} aria-label={t('common.delete', 'Delete')} onClick={() => onAction('delete', team)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
        </button>
      </span>
    </div>
  );
});

export default function TeamsTable({ teams, status, error, q, view, selected, activeId, showOwner, onToggle, onToggleAll, onOpen, onAction, onRetry, onClearSearch, onCreate }) {
  const { t } = useTranslation();
  const allSelected = teams.length > 0 && teams.every(x => selected.has(x.id));
  const someSelected = teams.some(x => selected.has(x.id));

  return (
    <div className="tm-table">
      <div className={`tm-head tm-grid${showOwner ? '' : ' no-owner'}`}>
        <span className="c-chk">
          <input type="checkbox" className="tm-check" checked={allSelected} disabled={!teams.length}
            ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
            onChange={() => onToggleAll(!allSelected)} aria-label={t('teams.selectPage', 'Select all on this page')} />
        </span>
        <span>{t('teams.colTeam', 'Team')}</span>
        <span>{t('teams.colRoster', 'Roster')}</span>
        <span>{t('teams.colElo', 'Avg ELO')}</span>
        <span>{t('teams.colCoach', 'Coach')}</span>
        {showOwner && <span>{t('teams.colOwner', 'Created by')}</span>}
        <span />
      </div>

      {status === 'loading' && !teams.length && [0, 1, 2, 3, 4].map(i => (
        <div key={i} className={`tm-row skel tm-grid${showOwner ? '' : ' no-owner'}`}>
          <span /><span className="tm-skeleton w70" /><span className="tm-skeleton w60" /><span className="tm-skeleton w40" /><span />{showOwner && <span />}<span />
        </div>
      ))}

      {status === 'error' && !teams.length && (
        <div className="tm-empty">
          <span className="tm-error">{error || t('teams.loadFailed', 'Could not load teams.')}</span>
          <button type="button" className="tm-btn" onClick={onRetry}>{t('common.retry', 'Retry')}</button>
        </div>
      )}

      {status !== 'loading' && status !== 'error' && !teams.length && (
        <div className="tm-empty">
          {q ? (
            <>
              <span>{t('teams.noMatch', 'No teams match “{{q}}”.', { q })}</span>
              <button type="button" className="tm-btn" onClick={onClearSearch}>{t('teams.clearSearch', 'Clear search')}</button>
            </>
          ) : view === 'archived' ? (
            <span>{t('teams.noArchivedTeams', 'No archived teams.')}</span>
          ) : (
            <>
              <span>{t('teams.noTeamsYet', 'No teams yet.')}</span>
              <button type="button" className="tm-btn primary" onClick={onCreate}>{t('teams.createTeam', 'Create Team')}</button>
            </>
          )}
        </div>
      )}

      {teams.map(team => (
        <TeamRow key={team.id} team={team} selected={selected.has(team.id)} active={activeId === team.id}
          showOwner={showOwner} view={view} onToggle={onToggle} onOpen={onOpen} onAction={onAction} />
      ))}
    </div>
  );
}
